import React from 'react';
import { useNavigate } from 'react-router-dom';
import Logo from '../components/Logo';
import './About.css';

const About: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="about-page">
      <header className="about-header">
        <button onClick={() => navigate(-1)} className="back-button">
          ← Back
        </button>
        <div className="brand-logo" onClick={() => navigate('/')}>
          <Logo height={34} />
        </div>
      </header>

      <main className="about-content">
        {/* Founder Story - Direct, no boxes */}
        <section className="founder-story">
          <h1>Why I Built Lomi</h1>

          <p>
            In the late 1990s, I walked through the gates of Medhane Alem School
            in Addis Ababa — a government school we called "Medesh." Above the
            entrance was a motto:
          </p>

          <blockquote>
            "Enter here the timeless fellowship of the human mind."
            <br />
            <span className="amharic-quote">"ከዚህ ከማይሞተው የሰው ልጅ ማደጊያ ስፍራ ግቡ"</span>
          </blockquote>

          <p>
            Inside those gates, I discovered the power of great teaching. My teachers
            made complex ideas simple. They had patience to explain until concepts clicked.
          </p>

          <p>
            I was lucky.
          </p>

          <div className="stats-highlight">
            <p>
              Most students sitting Ethiopia's national exams don't pass. Not because they
              aren't smart — because one teacher cannot sit with sixty students, because a
              past paper without an answer key teaches nothing, and because the exam is
              written in a language most students are still learning.
            </p>
          </div>

          <p>
            That last one is quieter than the others, and I think it matters more than we admit.
            A student can understand the concept perfectly and still lose the mark because the
            sentence defeated them first. Every question charges them twice — once to decode the
            English, again to understand the subject.
          </p>

          <p>
            That's why I built <strong>Lomi</strong>.
          </p>

          <p>
            Lomi exists to give every Ethiopian student what I had — personalized,
            patient, always-available help.
          </p>

          <p>
            There is a bigger argument to have about how schools should teach, and I want to spend
            years on it. But it takes a decade and it takes ministries, teachers and publishers. A
            student sitting the exam this year does not have a decade. So the order is: help them
            pass the exam in front of them — then go and change the system.
          </p>

          <p className="closing">
            Let's open that fellowship to everyone.
          </p>

          <div className="signature">
            — <a href="https://www.linkedin.com/in/yberhanu/" target="_blank" rel="noopener noreferrer">Yoseph Berhanu</a>, Founder
          </div>
        </section>

        {/* Mission - Simple */}
        <section className="mission">
          <h2>Our Mission</h2>
          <p className="mission-text">
            Every Ethiopian student deserves personalized help — regardless of where
            they go to school. Today that means the ESSLCE, which is the exam that
            decides the most for the most students, and the AAU Undergraduate
            Admission Test (UAT), which decides where the next ones go. It will not
            stop at those two either.
          </p>
          <p>
            Real past exam questions, each with its answer and an explanation. Something you can
            argue with until it makes sense. In English or Amharic. Available at eleven at night,
            which is when most studying actually happens.
          </p>
        </section>

        {/* What Lomi Does */}
        <section className="what-lomi-does">
          <h2>What Lomi Does</h2>
          <ul>
            <li><strong>Past papers, already sorted</strong> — years of real exam questions in one place, each with the correct answer and a written explanation, so you never have to hunt for a scan again</li>
            <li><strong>AAU UAT preparation</strong> — lessons, questions and full timed mock tests across all three sections of the entrance test: Quantitative, Verbal and Analytical</li>
            <li><strong>An AI you can argue with</strong> — ask again, ask differently, say you still don't understand. It won't just hand you the answer</li>
            <li><strong>In your language</strong> — not literal translation, but the explanation the way a classmate would give it, moving between Amharic and English</li>
            <li><strong>Unlimited practice</strong> — generate similar questions instantly for any topic</li>
            <li><strong>Knowledge checks</strong> — find your weak spots so you know exactly where to focus</li>
            <li><strong>Works on Telegram too</strong> — study on <a href="https://t.me/Lomistudybot" target="_blank" rel="noopener noreferrer">@Lomistudybot</a> from any phone</li>
          </ul>
        </section>

        {/* CTA */}
        <div className="cta">
          <button onClick={() => navigate('/login')} className="cta-button">
            Start Learning with Lomi
          </button>
        </div>

        {/* Footer */}
        <footer className="about-footer">
          <div className="footer-links">
            <button onClick={() => navigate('/contact')}>Contact</button>
            <a href="https://t.me/Lomistudybot" target="_blank" rel="noopener noreferrer">Telegram</a>
            <button onClick={() => navigate('/privacy')}>Privacy</button>
            <button onClick={() => navigate('/terms')}>Terms</button>
          </div>
          <p className="copyright">© {new Date().getFullYear()} Lomi</p>
        </footer>
      </main>
    </div>
  );
};

export default About;
